/**
 * ROTAS69 — drag-to-scroll for horizontal rails.
 *
 * Project rule: anything that scrolls sideways must be draggable with a mouse. The trigger is
 * the SCREEN SIZE that put the rail on screen, never the device type — a desktop browser
 * narrowed to a phone width gets the phone layout, and a touch-only rail would be unscrollable
 * there because a mouse has no swipe.
 *
 * Opt in declaratively: put data-r69-dragscroll on the scrolling element.
 *
 * Touch and trackpads are left alone — they already scroll natively, and hijacking them would
 * cost the momentum and rubber-banding the platform gives for free. Only mouse pointers drag.
 *
 * No class is toggled anywhere: state lives in inline styles on the rail itself, so this cannot
 * feed the theme's :has() invalidation (see the perf note on Horizon's :has() taxes).
 */
(() => {
  // Two sections on the homepage each pull this asset in, so the browser runs the file twice.
  // Everything therefore lives inside an IIFE behind a guard — a top-level `const` would throw
  // "Identifier has already been declared" on the second execution and kill the second run.
  if (window.__r69DragScroll) return;
  window.__r69DragScroll = true;

const DRAG_THRESHOLD = 4;

function wire(rail) {
  if (rail.dataset.r69DragWired === 'true') return;
  rail.dataset.r69DragWired = 'true';

  let dragging = false;
  let moved = false;
  let startX = 0;
  let startLeft = 0;
  // What this drag actually moves. A drag that STARTS on a card's photo moves the PHOTOS — the
  // card gallery's own scroller — and a drag starting anywhere else moves the rail. That is the
  // owner's split: photos scroll under the photo, cards scroll under the card body. Touch gets
  // this for free from native nested scrolling; this module is mouse-only.
  let target = rail;

  const stop = () => {
    if (!dragging) return;
    dragging = false;
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', stop);
    window.removeEventListener('pointercancel', stop);
    // Restoring the stylesheet's snap makes the release settle on a card, which is what the
    // gesture should feel like.
    target.style.scrollSnapType = '';
    target.style.userSelect = '';
    target.style.cursor = '';
    document.documentElement.style.userSelect = '';
    target = rail;
  };

  // Tracked on the WINDOW rather than through setPointerCapture. Capture retargets the click that
  // follows to the captured element, so a plain click on a card never reached the card itself —
  // no link navigation, no delegated handler, nothing. The window listeners give the same
  // keep-following-outside-the-rail behaviour with none of that.
  const onMove = (event) => {
    if (!dragging) return;
    const dx = event.clientX - startX;
    if (!moved && Math.abs(dx) > DRAG_THRESHOLD) {
      moved = true;
      // Everything that makes this a drag waits for the threshold: below it the press is still a
      // click, and flipping the cursor there made a still click look like it had started a drag.
      // Mandatory snap also fights every intermediate scrollLeft, so it is off for the duration.
      target.style.scrollSnapType = 'none';
      target.style.userSelect = 'none';
      target.style.cursor = 'grabbing';
    }
    if (!moved) return;
    target.scrollLeft = startLeft - dx;
    event.preventDefault();
  };

  rail.addEventListener('pointerdown', (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;

    // The press point picks the scroller: a horizontally scrollable card gallery under the
    // pointer takes the drag; otherwise the rail itself. slideshow-slides is the theme card's
    // gallery scroller; [data-r69-gallery] is the opt-in for bespoke card galleries.
    const gallery = event.target instanceof Element ? event.target.closest('slideshow-slides, [data-r69-gallery]') : null;
    target = gallery && rail.contains(gallery) && gallery.scrollWidth > gallery.clientWidth + 4 ? gallery : rail;
    if (target === rail && rail.scrollWidth <= rail.clientWidth) return; // laid out as a column, nothing to drag

    dragging = true;
    moved = false;
    startX = event.clientX;
    startLeft = target.scrollLeft;
    // Selection is off for the WHOLE press, not from the 4px threshold: the browser starts
    // selecting on the very first pixel of movement, so titles and prices turned blue on every
    // drag and the selection stayed until the next click — which opened a card (owner,
    // 06.09.2026). Set on the root, because a drag runs past the rail's edge; restored in stop().
    document.documentElement.style.userSelect = 'none';
    const selection = window.getSelection ? window.getSelection() : null;
    if (selection && selection.rangeCount) selection.removeAllRanges();
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
  });

  // A rail is usually made of links. Without this, letting go after a drag navigates.
  rail.addEventListener(
    'click',
    (event) => {
      if (!moved) return;
      event.preventDefault();
      event.stopPropagation();
      moved = false;
    },
    true
  );

  rail.addEventListener('dragstart', (event) => event.preventDefault());
}

function wireAll(root) {
  (root || document).querySelectorAll('[data-r69-dragscroll]').forEach(wire);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => wireAll());
} else {
  wireAll();
}

// Rails whose cards arrive from a Section Rendering fetch can appear after this runs.
window.r69DragScroll = wireAll;
})();
